import { useState, useEffect, useCallback } from "react";
import { Video, Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface VibeVideoNudgeProps {
  userProfile: {
    has_intro_video?: boolean | null;
  } | null;
  /** Trigger nudge on interaction events */
  triggerEvent?: "pop" | "like" | "match" | null;
}

const NUDGE_SESSION_KEY = "vibe-video-nudge-shown";

// Randomly cycle through encouraging messages
const MESSAGES = [
  { title: "✨ People love seeing real vibes", subtitle: "A 7-sec intro video helps you stand out" },
  { title: "🎬 Show off your glow!", subtitle: "Videos get 3x more matches" },
  { title: "💫 Let your personality shine", subtitle: "Add a quick video intro" },
  { title: "🔥 Make a lasting first impression", subtitle: "Your vibe video is your superpower" },
];

const VibeVideoNudge = ({ userProfile, triggerEvent }: VibeVideoNudgeProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState(MESSAGES[0]);
  const navigate = useNavigate();

  // Trigger nudge based on interaction events (like, pop, match)
  const showNudge = useCallback(() => {
    const hasVideo = userProfile?.has_intro_video;
    const alreadyShown = sessionStorage.getItem(NUDGE_SESSION_KEY);

    if (!hasVideo && !alreadyShown) {
      // Pick a random message
      setMessage(MESSAGES[Math.floor(Math.random() * MESSAGES.length)]);
      setIsVisible(true);
      sessionStorage.setItem(NUDGE_SESSION_KEY, "true");
    }
  }, [userProfile?.has_intro_video]);

  // Listen for trigger events
  useEffect(() => {
    if (triggerEvent) {
      // Small delay to let the action complete visually first
      const timer = setTimeout(() => {
        showNudge();
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [triggerEvent, showNudge]);

  const handleDismiss = () => {
    setIsVisible(false);
  };

  const handleAddVideo = () => {
    setIsVisible(false);
    navigate("/profile");
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-24 left-4 right-4 z-40 animate-slide-up">
      <div className="bg-gradient-to-r from-pop/95 to-pink-500/95 rounded-2xl p-4 shadow-xl backdrop-blur-sm border border-white/20">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-white" />
        </button>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium text-sm">
              {message.title}
            </p>
            <p className="text-white/80 text-xs mt-0.5">
              {message.subtitle}
            </p>
          </div>

          <button
            onClick={handleAddVideo}
            className="shrink-0 px-3 py-1.5 rounded-full bg-white text-pop text-sm font-medium hover:bg-white/90 transition-colors flex items-center gap-1.5"
          >
            <Video className="w-3.5 h-3.5" />
            <span>Add vibe</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VibeVideoNudge;
